import React, { createRef, useEffect, useState } from 'react'
import {
  InvalidEmailInformation,
  LoginContent,
  LoginSubTitle,
  LoginTitle,
  RequestDemoLink,
} from '@features/LoginModal/styles'
import { Button } from '@thriveglobal/thrive-ui-lib'
import { COLORS } from '@enums/colors'
import { useTranslation } from 'react-i18next'
import useAuth from '@hooks/use-auth'
import InputText from '@elements/InputText'
import { trackCustomPageEvent } from '@api/segment/page'
import { SegmentPage } from '@enums/segment'

interface Props {
  email: string
  transition: () => void
  viewState?: LOGIN_MODAL_FORGOT_PASSWORD_STATES
}
export enum LOGIN_MODAL_FORGOT_PASSWORD_STATES {
  'CONFIRMATION',
  'VERIFICATION',
}

const LoginModalForgotPassword = ({ email, transition, viewState }: Props) => {
  const [transitionState, updateState] =
    useState<LOGIN_MODAL_FORGOT_PASSWORD_STATES>(
      viewState || LOGIN_MODAL_FORGOT_PASSWORD_STATES.CONFIRMATION
    )
  const passwordInputRef = createRef<HTMLInputElement>()
  const verificationCodeInputRef = createRef<HTMLInputElement>()
  const [formError, setFormError] = useState(null)

  const { t } = useTranslation()
  const { sendResetPasswordEmail, resetPassword, isLoading } = useAuth()

  useEffect(() => trackCustomPageEvent(SegmentPage.resetPassword), []);

  const sendResetEmail = async () => {
    try {
      await sendResetPasswordEmail(email)
      updateState(LOGIN_MODAL_FORGOT_PASSWORD_STATES.VERIFICATION)
    } catch (e) {
      setFormError(e.message)
    }
  }

  const onSubmit = async () => {
    const verificationCode = verificationCodeInputRef.current?.value
    const password = passwordInputRef.current?.value
    if (verificationCode !== undefined && password !== undefined) {
      try {
        await resetPassword(email, password, verificationCode)
        transition()
      } catch (e) {
        setFormError(e.message)
      }
    }
  }

  const goBack = () => {
    transition()
  }

  return (
    <LoginContent>
      <LoginTitle>{t('login_modal_title_reset_password')}</LoginTitle>
      <LoginSubTitle>{t('login_modal_sub_title_reset_password')}</LoginSubTitle>
      {transitionState === LOGIN_MODAL_FORGOT_PASSWORD_STATES.CONFIRMATION && (
        <>
          <Button
            loading={isLoading}
            onClick={sendResetEmail}
            margin={'20px 0 0 0'}
            data-testid={'btn-send-reset-email'}
          >
            {t('login_modal_button_reset_password')}
          </Button>
        </>
      )}
      {transitionState === LOGIN_MODAL_FORGOT_PASSWORD_STATES.VERIFICATION && (
        <>
          <InputText
            ref={verificationCodeInputRef}
            placeholder={t('login_modal_reset_password_verification_code')}
            onSubmit={onSubmit}
            isValid={false}
          />
          <InputText
            ref={passwordInputRef}
            placeholder={t('login_modal_reset_password_new')}
            onSubmit={onSubmit}
            isValid={false}
            type={'password'}
          />
          <Button
            data-testid={'btn-send-submit'}
            loading={isLoading}
            onClick={onSubmit}
          >
            {t('login_modal_button_change_password')}
          </Button>
        </>
      )}
      {formError && (
        <InvalidEmailInformation>{formError}</InvalidEmailInformation>
      )}
      <RequestDemoLink onClick={goBack} color={COLORS.teal}>
        {t('login_modal_back_to_login')}
      </RequestDemoLink>
    </LoginContent>
  )
}

export default LoginModalForgotPassword
